import { ActionTree, MutationTree, GetterTree } from 'vuex';
import { MerchantState } from '@/store/modules/merchantTypes';
import { IRouterProps } from '@/router/interfaces/router-props.interface';
import { IWidgetSettings } from '@/models/Widget/settings';
import { IConnectOauth, IProvider } from '@/models/provider';
import MerchantService from '@/api/MerchantService';
import segmentIo from '@/utils/segment.io';

import Module from '../interfaces/module.interface';
import { RootState } from '../types';
import { setExternalOauthSession } from '@/router/router-utils';
import { app } from '@/main';
import { IBusinessType } from '@/models/business';
import { isOnlineProvider } from '@/utils/provider';
import logger from '@/utils/logger';

export const state: MerchantState = new MerchantState();

const merchantService = new MerchantService();

const factory = async (context: IRouterProps) => {
  const load = async () => {
    const country =
      context.store.getters['internationalization/getCountrySettings'];
    const isAuthenticated = !!context.store.getters['manager/getExtraInfo'];
    if (context.auth === 'manager' && country && isAuthenticated) {
      merchantService.listProviders().then(response => {
        context.store.dispatch(
          'merchant/SET_PROVIDERS_LIST',
          response.data.providers
        );
        context.store.dispatch(
          'merchant/SET_PROVIDERS_COUNTRY_LIST',
          response.data.providers
        );
      });
      const responseConnected = await merchantService.listConnectedProviders();

      const businessInfo =
        context.store.getters['business/getBusinessInfo'].business;
      const isImpersonated = context.store.getters['manager/isImpersonated'];

      if (
        businessInfo &&
        businessInfo.onlineMerchantSignup &&
        responseConnected.data.length &&
        !isImpersonated &&
        !document.location.href.includes('/manager') &&
        !document.location.href.includes('business-onboarding') &&
        isOnlineProvider(responseConnected.data[0].provider)
      ) {
        // An invited owner joining an existing online business has no access to
        // the provider's OAuth connection, so this call returns 401. Tolerate it
        // (dedicated instance with the 401->signout interceptor ejected) and skip
        // the online-onboarding redirect instead of logging the owner out — an
        // invite doesn't go through the provider OAuth flow (TWISM-18461).
        const connectService = new MerchantService();
        connectService.disableInterception();
        let oauthSettings = null;
        try {
          oauthSettings = await connectService.getConnectSettings(
            responseConnected.data[0].id
          );
        } catch (err) {
          // Tolerate only the invited owner's 401 (no provider OAuth access).
          // A transient 5xx must still propagate, or a legit online merchant
          // mid-onboarding gets silently skipped past the required redirect.
          if (err && err.response && err.response.status !== 401) {
            throw err;
          }
          oauthSettings = null;
        }
        const hasNeverFinishedOnboarding =
          !!oauthSettings &&
          !!oauthSettings.data &&
          !!oauthSettings.data.settings &&
          !Object.keys(oauthSettings.data.settings).includes('enabled');
        if (hasNeverFinishedOnboarding) {
          setExternalOauthSession(IBusinessType.ONLINE);
          app.$router.push({
            path: '/manager',
            query: {
              onlineMerchantWidgetStep: 'true',
              onlineMerchantRewardStep:
                businessInfo.coinPurchaseRatio === '0.00' ? 'true' : undefined
            }
          });
        }
      }

      context.store.dispatch(
        'merchant/SET_CONNECTED_PROVIDERS_LIST',
        responseConnected.data
      );

      if (businessInfo) {
        logger.identifyBusiness(businessInfo.id);
        segmentIo.identifyBusiness(businessInfo);
      }
    } else {
      throw new Error('Not Initialized');
    }
  };

  await load();

  context.store.watch(
    (s, getters) => getters['internalization/getCountrySettings'],
    load
  );

  context.store.watch(
    (s, getters) => getters['business/getBusinessState'],
    load
  );
};

const actions: ActionTree<MerchantState, RootState> = {
  SET_PROVIDERS_LIST(context, payload: IProvider[]) {
    context.commit('setProvidersList', payload);
  },
  SET_CONNECTED_PROVIDERS_LIST(context, payload: IConnectOauth[]) {
    context.commit('setConnectedProvidersList', payload);
  },
  SET_PROVIDERS_COUNTRY_LIST(context, payload: IProvider[]) {
    context.commit('setProvidersCountryList', payload);
  },
  UPDATE_WIDGET_PROVIDER_SETTINGS(context, payload: Partial<IWidgetSettings>) {
    context.commit('updateWidgetProviderSettings', payload);
  },
  SET_PROVIDERS_TO_UPDATE(context, payload: IConnectOauth[]) {
    context.commit('setProvidersToUpdate', payload);
  }
};

const mutations: MutationTree<MerchantState> = {
  setProvidersList(state, payload: IProvider[]) {
    state.initialized = true;
    state.providers = payload;
  },
  setConnectedProvidersList(state, payload: IConnectOauth[]) {
    state.initialized = true;
    state.connectedProviders = payload;
  },
  setProvidersCountryList(state, payload: IProvider[]) {
    state.initialized = true;
    state.providersCountry = payload;
  },
  updateWidgetProviderSettings(state, payload: Partial<IWidgetSettings>) {
    state.providers = state.providers.map(provider => {
      if (provider.connected && provider.features.includes('widget')) {
        return {
          ...provider,
          settings: {
            ...provider.settings,
            ...payload
          }
        };
      }

      return provider;
    });
  },
  setProvidersToUpdate(state, payload: IConnectOauth[]) {
    state.providersToUpdate = payload;
  }
};

const getters: GetterTree<MerchantState, RootState> = {
  getProvidersList(state) {
    return state.providers;
  },
  getActiveProvidersList(state) {
    return state.providers.filter(item => item.status === 'active');
  },
  getConnectedProviderWithWidgetSettings(state) {
    return state.providers.find(
      provider => provider.connected && provider.features.includes('widget')
    );
  },
  getConnectedProvidersList(state) {
    return state.connectedProviders;
  },
  getCurrentProvider(state) {
    return state.providers.find(item => item.connected);
  },
  isInitialized(state) {
    return state.initialized;
  },
  getProvidersCountryList(state) {
    return state.providersCountry;
  },
  getActiveProvidersCountryList(state) {
    return state.providersCountry.filter(item => item.status === 'active');
  },
  getProvidersToUpdate(state) {
    return state.providersToUpdate;
  }
};

const namespaced: boolean = true;

export const merchant: Module<MerchantState, RootState> = {
  namespaced,
  initializer: {
    factory,
    provide: 'MerchantStore',
    requires: ['InternationalizationStore'],
    on: 'BeforeEachRoute'
  },
  state,
  actions,
  mutations,
  getters
};
